import { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { snapshot, type SnapshotGoal, type SnapshotGoalStatus } from 'services/api';

import { readSnapshotApiError, SnapshotEditorShell } from './SnapshotEditorShell';

type GoalLifecycleModalProps = {
  goal: SnapshotGoal | null;
  targetStatus: SnapshotGoalStatus | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (goal: SnapshotGoal) => Promise<void> | void;
};

const copy: Record<SnapshotGoalStatus, { title: string; body: string; action: string }> = {
  active: {
    title: 'Reactivate this goal?',
    body: 'Your previous progress remains intact. Daily check-ins begin again tomorrow.',
    action: 'Reactivate goal',
  },
  paused: {
    title: 'Pause this goal?',
    body: 'Progress stays intact and check-in reminders stop until you reactivate it.',
    action: 'Pause goal',
  },
  completed: {
    title: 'Mark this goal complete?',
    body: 'The goal moves into completed history and will no longer ask for daily check-ins.',
    action: 'Mark complete',
  },
  retired: {
    title: 'Retire this goal?',
    body: 'It will disappear from Snapshot. Its revisions and progress remain preserved in history, and retirement cannot be undone here.',
    action: 'Retire goal',
  },
};

export const GoalLifecycleModal = ({ goal, targetStatus, onOpenChange, onSaved }: GoalLifecycleModalProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (goal && targetStatus) {
      setSaving(false);
      setError('');
    }
  }, [goal, targetStatus]);

  if (!targetStatus) return null;
  const content = copy[targetStatus];

  const save = async () => {
    if (!goal) return;
    setSaving(true);
    setError('');
    try {
      const saved = await snapshot.updateGoalStatus(goal.id, targetStatus);
      await onSaved(saved);
      onOpenChange(false);
    } catch (saveError) {
      setError(readSnapshotApiError(saveError).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SnapshotEditorShell
      open={Boolean(goal)}
      title={content.title}
      description={content.body}
      onOpenChange={onOpenChange}
      maxWidthClassName="max-w-[500px]"
    >
      {error ? <p role="alert" className="mb-4 rounded-xl bg-foundation-orange-50 px-3 py-2 text-sm text-foundation-orange-900">{error}</p> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" size="auto" className="min-h-11" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button type="button" variant={targetStatus === 'retired' ? 'secondary' : 'default'} size="auto" className="min-h-11" disabled={saving} onClick={save}>{saving ? 'Saving…' : content.action}</Button>
      </div>
    </SnapshotEditorShell>
  );
};
